import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { supabase } from './config/supabase';
import { isRecoveryLocation, resetPasswordLocation } from './utils/passwordRecovery';
import { Toaster } from 'react-hot-toast';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import NotFound from './pages/NotFound';
import RealTimeNotifications from './components/RealTimeNotifications';
import MobileTabBar from './components/MobileTabBar';
import PageMeta from './seo/PageMeta';
import WhatsAppAssistButton from './components/WhatsAppAssistButton';
import CookieBanner from './components/CookieBanner';
import { ROUTE_CONFIGS } from './seo/routes';
import { GUIDE_CONFIGS } from './seo/guides';
import { isSharedTransfersEnabled, isTeamPanelEnabled } from './config/features';

// Route-level code splitting keeps the initial bundle small. Home loads
// eagerly (first paint); heavier or less-visited routes load on demand.
const Create = lazy(() => import('./pages/Create'));
const MyTrips = lazy(() => import('./pages/MyTrips'));
const Trips = lazy(() => import('./pages/Trips'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const Messages = lazy(() => import('./pages/Messages'));
const Admin = lazy(() => import('./pages/Admin'));
const Team = lazy(() => import('./pages/Team'));
const BusTickets = lazy(() => import('./pages/BusTickets'));
const PrivateTransfer = lazy(() => import('./pages/PrivateTransfer'));
const ManageBooking = lazy(() => import('./pages/ManageBooking'));
const BookingConditions = lazy(() => import('./pages/BookingConditions'));
const Privacy = lazy(() => import('./pages/Privacy'));
const RouteLanding = lazy(() => import('./pages/RouteLanding'));
const GuidePage = lazy(() => import('./pages/GuidePage'));
const Offices = lazy(() => import('./pages/Offices'));
const Schedule = lazy(() => import('./pages/Schedule'));

// Legacy links (bookmarks, old emails) used /#private-transfer before the
// quote form got its own route. Send them to the page instead.
function LegacyPrivateTransferRedirect() {
  const location = useLocation();
  if (location.pathname === '/' && location.hash === '#private-transfer') {
    return <Navigate to="/private-transfer" replace />;
  }
  return null;
}

// Dashboard "Send recovery" and Site URL fallback both land on `/` with
// `type=recovery` in the hash (or a PASSWORD_RECOVERY session after PKCE).
// Home would otherwise swallow that and look like a broken reset link.
function PasswordRecoveryRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname === '/reset-password') return;
    if (!isRecoveryLocation(location)) return;
    navigate(resetPasswordLocation(location), { replace: true });
  }, [location, navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event !== 'PASSWORD_RECOVERY') return;
      if (window.location.pathname === '/reset-password') return;
      navigate(resetPasswordLocation(window.location), { replace: true });
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  return null;
}

// React Router doesn't scroll to #hash targets on navigation; in-page
// anchors (e.g. the shared-trip schedule) rely on this.
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Retry briefly: the target section may still be rendering.
    let attempts = 0;
    const tryScroll = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (attempts++ < 10) {
        setTimeout(tryScroll, 100);
      }
    };
    tryScroll();
  }, [location.pathname, location.hash]);
  return null;
}

// React Router preserves scroll position across navigations; reset to top on
// route change unless a hash anchor is in play (ScrollToHash handles those).
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

export function AppRoutes() {
  const sharedEnabled = isSharedTransfersEnabled();
  const teamPanelEnabled = isTeamPanelEnabled();
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      {/* SEO landing pages — one indexable route per config entry,
          reusing the live booking components via RouteLanding. */}
      {ROUTE_CONFIGS.map((r) => (
        <Route
          key={r.slug}
          path={`/${r.slug}`}
          element={<RouteLanding route={r} />}
        />
      ))}
      <Route path="/bus" element={<BusTickets />} />
      <Route path="/private-transfer" element={<PrivateTransfer />} />
      <Route path="/manage/:orderNumber" element={<ManageBooking />} />
      <Route path="/schedule" element={<Schedule />} />
      <Route path="/booking-conditions" element={<BookingConditions />} />
      <Route path="/privacy" element={<Privacy />} />
      {GUIDE_CONFIGS.map((g) => (
        <Route key={g.slug} path={`/${g.slug}`} element={<GuidePage guide={g} />} />
      ))}
      <Route path="/offices" element={<Offices />} />
      <Route path="/my-trips" element={<MyTrips />} />
      {/* Shared Transfers routes stay implemented but are gated behind
          the launch flag. When disabled they redirect home so old links
          and the code path both stay safe. */}
      <Route
        path="/create"
        element={sharedEnabled ? <Create /> : <Navigate to="/" replace />}
      />
      <Route
        path="/trips"
        element={sharedEnabled ? <Trips /> : <Navigate to="/" replace />}
      />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/messages" element={<Messages />} />
      <Route path="/admin" element={<Admin />} />
      {/* Riverside Team ops panel. Flag-gated so the route can ship
          before the team account is provisioned; the page itself gates
          on the account, and ops-console gates on the server. */}
      <Route
        path="/team"
        element={teamPanelEnabled ? <Team /> : <Navigate to="/" replace />}
      />
      {/* Stripe return target for the shared-trip join flow. */}
      <Route path="/success" element={<Navigate to="/my-trips" replace />} />
      <Route path="/pricing-demo" element={<Navigate to="/" replace />} />
      <Route path="/notification-test" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App flex flex-col min-h-screen">
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-riverside-red focus:shadow-md focus:ring-2 focus:ring-riverside-red"
          >
            Skip to content
          </a>
          <LegacyPrivateTransferRedirect />
          <PasswordRecoveryRedirect />
          <ScrollToHash />
          <ScrollToTop />
          <PageMeta />
          <Header />
          <RealTimeNotifications />
          <main id="main-content" tabIndex={-1} className="main-content flex-1 pt-[72px] md:pt-[84px] pb-16 md:pb-0 focus:outline-none">
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-32">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-riverside-red" />
                </div>
              }
            >
              <AppRoutes />
            </Suspense>
          </main>
          <Footer />
          <MobileTabBar />
          <WhatsAppAssistButton />
          <CookieBanner />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App; 